
import api, {
  buildCompanyScopeHeaders,
} from "./api";


export const DEFAULT_TARGET_COVERAGE_DAYS = 14;


function normalizeOptionalString(value) {
  if (typeof value !== "string") {
    return "";
  }

  return value.trim();
}


function normalizePositiveNumber(
  value,
  fieldName,
) {
  if (
    value === null
    || value === undefined
    || value === ""
  ) {
    return null;
  }

  const parsed = Number(value);

  if (
    !Number.isFinite(parsed)
    || parsed <= 0
  ) {
    throw new TypeError(
      `${fieldName} must be greater than zero.`,
    );
  }

  return parsed;
}


export function normalizeProductUuids(value) {
  const candidates = Array.isArray(value)
    ? value
    : String(value || "")
      .split(/[\n,;]+/u);

  return [
    ...new Set(
      candidates
        .map((item) => String(item || "").trim())
        .filter(Boolean),
    ),
  ];
}


export function buildReplenishmentProposalPayload({
  supplierUuid = "",
  productUuids = [],
  targetCoverageDays =
    DEFAULT_TARGET_COVERAGE_DAYS,
  metadata = {},
} = {}) {
  const normalizedSupplierUuid =
    normalizeOptionalString(supplierUuid);

  const normalizedProducts =
    normalizeProductUuids(productUuids);

  const normalizedCoverage =
    normalizePositiveNumber(
      targetCoverageDays,
      "targetCoverageDays",
    );

  if (
    metadata === null
    || typeof metadata !== "object"
    || Array.isArray(metadata)
  ) {
    throw new TypeError(
      "metadata must be an object.",
    );
  }

  const payload = {
    metadata: {
      source: "supply_chain_replenishment_admin",
      ...metadata,
    },
  };

  if (normalizedSupplierUuid) {
    payload.supplier_uuid =
      normalizedSupplierUuid;
  }

  if (normalizedProducts.length) {
    payload.product_uuids =
      normalizedProducts;
  }

  if (normalizedCoverage !== null) {
    payload.target_coverage_days =
      normalizedCoverage;
  }

  return payload;
}


export function getReplenishmentErrorMessage(
  error,
  fallback =
    "Impossible de générer la proposition de réassort.",
) {
  const responseData = error?.response?.data;

  if (
    typeof responseData?.error?.message
      === "string"
    && responseData.error.message.trim()
  ) {
    return responseData.error.message.trim();
  }

  if (
    typeof responseData?.message === "string"
    && responseData.message.trim()
  ) {
    return responseData.message.trim();
  }

  if (
    typeof responseData?.error === "string"
    && responseData.error.trim()
  ) {
    return responseData.error.trim();
  }

  if (
    typeof error?.message === "string"
    && error.message.trim()
  ) {
    return error.message.trim();
  }

  return fallback;
}


export async function createReplenishmentProposal({
  companyUuid,
  supplierUuid = "",
  productUuids = [],
  targetCoverageDays =
    DEFAULT_TARGET_COVERAGE_DAYS,
  metadata = {},
} = {}) {
  const payload =
    buildReplenishmentProposalPayload({
      supplierUuid,
      productUuids,
      targetCoverageDays,
      metadata,
    });

  const response = await api.post(
    "/supply-chain/replenishment/proposals",
    payload,
    {
      headers: buildCompanyScopeHeaders(
        companyUuid,
      ),
    },
  );

  return response.data;
}


export function buildReplenishmentPurchaseOrderPayload({
  supplierUuid = "",
  productUuids = [],
  targetCoverageDays =
    DEFAULT_TARGET_COVERAGE_DAYS,
  orderReference = "",
  metadata = {},
} = {}) {
  const payload =
    buildReplenishmentProposalPayload({
      supplierUuid,
      productUuids,
      targetCoverageDays,
      metadata,
    });

  const normalizedOrderReference =
    normalizeOptionalString(orderReference);

  if (!normalizedOrderReference) {
    throw new TypeError(
      "orderReference is required.",
    );
  }

  return {
    ...payload,
    order_reference: normalizedOrderReference,
  };
}


export async function prepareReplenishmentPurchaseOrder({
  companyUuid,
  supplierUuid = "",
  productUuids = [],
  targetCoverageDays =
    DEFAULT_TARGET_COVERAGE_DAYS,
  orderReference = "",
  metadata = {},
} = {}) {
  const payload =
    buildReplenishmentPurchaseOrderPayload({
      supplierUuid,
      productUuids,
      targetCoverageDays,
      orderReference,
      metadata,
    });

  const response = await api.post(
    "/supply-chain/replenishment/purchase-orders",
    payload,
    {
      headers: buildCompanyScopeHeaders(
        companyUuid,
      ),
    },
  );

  return response.data;
}
